import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Crown, Users, Save, Shield, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CARGOS = [
  { key: "veneravel_id", label: "Venerável Mestre", required: true, hint: "Preside a Loja, dirige os trabalhos rituais." },
  { key: "primeiro_vigilante_id", label: "1º Vigilante", required: true, hint: "Substitui o Venerável, dirige os Companheiros." },
  { key: "segundo_vigilante_id", label: "2º Vigilante", required: true, hint: "Dirige a Câmara de Aprendizes." },
  { key: "orador_id", label: "Orador", required: false, hint: "Guardião da Lei; profere pareceres jurídicos." },
  { key: "secretario_id", label: "Secretário", required: true, hint: "Lavra atas (balaústres) e correspondência." },
  { key: "tesoureiro_id", label: "Tesoureiro", required: true, hint: "Arrecada capitações e gere as finanças." },
  { key: "chanceler_id", label: "Chanceler", required: false, hint: "Guarda arquivos, selos e diplomas." },
  { key: "hospitaleiro_id", label: "Hospitaleiro", required: false, hint: "Cuida do Tronco de Beneficência." },
];

export const DiretoriaGestaoView = ({ members = [], currentUser, currentLoja, treasurySettings, onReload }) => {
  const [assignments, setAssignments] = useState({});
  const [gestaoLabel, setGestaoLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [transferEmail, setTransferEmail] = useState("");
  const [transferring, setTransferring] = useState(false);

  const isMaster = currentUser?.is_master_loja || currentUser?.role === "master_loja" || currentUser?.role === "super_admin";

  const startYear = treasurySettings?.gestao_start_year || new Date().getFullYear();
  const endYear = startYear + 1;
  const suggestedLabel = `${startYear}/${endYear}`;

  useEffect(() => {
    if (!gestaoLabel) setGestaoLabel(suggestedLabel);
  }, [suggestedLabel]);

  // Carrega diretoria vigente
  useEffect(() => {
    axios.get(`${API}/lojas/gestao/atual`).then((r) => {
      const dir = r.data?.diretoria || {};
      setAssignments(dir);
      if (r.data?.label) setGestaoLabel(r.data.label);
    }).catch(() => {});
  }, []);

  const memberOptions = useMemo(() => {
    return [...(members || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR"));
  }, [members]);

  const setAssign = (key, val) => setAssignments((prev) => ({ ...prev, [key]: val || null }));

  const usedIds = useMemo(() => new Set(Object.values(assignments).filter(Boolean)), [assignments]);

  const handleSave = async () => {
    if (!isMaster) {
      toast.error("Apenas o Master da Loja pode alterar a Diretoria.");
      return;
    }
    // Confirmação antes de aplicar (evita clique acidental)
    const chosenCount = Object.values(assignments).filter(Boolean).length;
    const msg =
      `Confirmar a Diretoria da Gestão ${gestaoLabel || suggestedLabel}?\n\n` +
      `• ${chosenCount} cargo(s) serão aplicados\n` +
      `• Cargos anteriores serão liberados no Quadro de Obreiros\n` +
      `• Esta ação NÃO afeta seu acesso como Master da Loja`;
    if (!window.confirm(msg)) return;

    setSaving(true);
    try {
      const payload = { ...assignments, gestao_label: gestaoLabel || suggestedLabel };
      const res = await axios.post(`${API}/lojas/gestao/diretoria`, payload);
      toast.success(res.data.message);
      onReload && onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar diretoria.");
    } finally {
      setSaving(false);
    }
  };

  const handleTransferMaster = async () => {
    if (!transferEmail.trim()) {
      toast.error("Informe o e-mail do novo Master da Loja.");
      return;
    }
    if (!window.confirm(`Transferir o cargo de Master da Loja para ${transferEmail}? Você perderá o acesso administrativo permanente.`)) return;
    setTransferring(true);
    try {
      const res = await axios.post(`${API}/lojas/master/transfer`, { new_master_email: transferEmail.trim() });
      toast.success(res.data.message);
      setTransferEmail("");
      onReload && onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao transferir.");
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="diretoria-gestao-view">
      {/* Hero */}
      <div className="luxury-card p-6 flex items-center justify-between gap-4 border-l-4 border-l-[#D4AF37]">
        <div>
          <span className="text-xs text-[#D4AF37] uppercase tracking-widest font-bold flex items-center gap-2">
            <Crown className="w-3.5 h-3.5" />
            Diretoria da Gestão Maçônica
          </span>
          <h2 className="text-2xl font-serif font-bold text-white mt-1">
            Eleição de Cargos • {currentLoja?.name || "Loja"}
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Escolha os Irmãos que ocuparão cada cargo nesta gestão. Ao salvar, os cargos anteriores são liberados e os novos aplicados no Quadro de Obreiros.
          </p>
        </div>
      </div>

      {/* Explicação Master vs Venerável */}
      <div className="p-4 rounded-2xl bg-gradient-to-br from-[#D4AF37]/10 via-[#131C2E] to-[#131C2E] border border-[#D4AF37]/30" data-testid="master-explanation-card">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" />
          <div className="text-xs text-slate-300 leading-relaxed">
            <p className="text-white font-bold mb-1">Master da Loja ≠ Venerável Mestre</p>
            <p>
              <b className="text-[#D4AF37]">Master da Loja</b> é o dono da conta na plataforma (quem cadastrou a Loja) — acesso administrativo permanente, mesmo trocando de cargo.<br/>
              <b className="text-[#D4AF37]">Venerável Mestre</b> é um cargo eleito a cada gestão (~12 meses) e pode ser trocado normalmente aqui embaixo, sem afetar o acesso administrativo.
            </p>
            <p className="text-emerald-300 mt-2 font-semibold">
              Seu papel atual: {currentUser?.is_master_loja || currentUser?.role === "master_loja" ? "Master da Loja (permanente)" : "Usuário padrão"}
            </p>
          </div>
        </div>
      </div>

      {/* Grid de Cargos */}
      <div className="luxury-card p-6 space-y-4">
        <div className="flex items-end justify-between gap-3 pb-3 border-b border-slate-800">
          <div>
            <label className="text-[10px] uppercase text-slate-400 tracking-wider font-semibold block">Identificação da Gestão</label>
            <input
              data-testid="input-gestao-label"
              value={gestaoLabel}
              onChange={(e) => setGestaoLabel(e.target.value)}
              placeholder={suggestedLabel}
              className="mt-1 bg-[#080C14] border border-slate-700 rounded-lg px-3 py-1.5 text-sm text-[#D4AF37] font-mono focus:border-[#D4AF37] outline-none"
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !isMaster}
            data-testid="btn-save-diretoria"
            className="bg-[#D4AF37] hover:bg-[#C5A028] disabled:opacity-40 disabled:cursor-not-allowed text-slate-950 font-bold px-4 py-2 rounded-xl text-xs uppercase tracking-wider inline-flex items-center gap-2"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? "Salvando..." : "Aplicar Diretoria"}
          </button>
        </div>

        {memberOptions.length === 0 ? (
          <div className="p-8 rounded-xl border border-dashed border-slate-700 text-center text-slate-500 text-sm">
            Cadastre Irmãos em <b>Membros da Loja</b> antes de eleger a Diretoria.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {CARGOS.map(({ key, label, required, hint }) => (
              <div key={key} className="p-3 rounded-xl bg-[#080C14] border border-slate-800" data-testid={`diretoria-slot-${key}`}>
                <label className="text-[11px] uppercase text-[#D4AF37] tracking-wider font-bold flex items-center gap-1.5">
                  <Users className="w-3 h-3" />
                  {label} {required && <span className="text-rose-400">*</span>}
                </label>
                <select
                  data-testid={`select-cargo-${key}`}
                  value={assignments[key] || ""}
                  onChange={(e) => setAssign(key, e.target.value)}
                  disabled={!isMaster}
                  className="mt-1.5 w-full bg-[#131C2E] border border-slate-700 rounded-lg px-2 py-2 text-xs text-white focus:border-[#D4AF37] outline-none disabled:opacity-50"
                >
                  <option value="">— Vago —</option>
                  {memberOptions.map((m) => (
                    <option key={m.id} value={m.id} disabled={usedIds.has(m.id) && assignments[key] !== m.id}>
                      {m.name} · CIM {m.cim} · {m.degree}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-500 italic mt-1">{hint}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Transferência de Master */}
      {isMaster && (
        <div className="luxury-card p-6 border border-rose-500/20" data-testid="transfer-master-card">
          <div className="flex items-center gap-2 mb-2">
            <RefreshCw className="w-4 h-4 text-rose-400" />
            <h3 className="font-serif font-bold text-base text-white">Transferir Master da Loja</h3>
          </div>
          <p className="text-[11px] text-slate-400 mb-3">
            Use esta ação se você for sair da Loja: transfere o acesso administrativo permanente para outro usuário desta Loja. Você perde o status de Master, mas continua como membro normal.
          </p>
          <div className="flex items-stretch gap-2">
            <input
              type="email"
              value={transferEmail}
              onChange={(e) => setTransferEmail(e.target.value)}
              placeholder="email-do-novo-master@lojamaconica.org"
              data-testid="input-transfer-email"
              className="flex-1 bg-[#080C14] border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-rose-500 outline-none font-mono"
            />
            <button
              onClick={handleTransferMaster}
              disabled={transferring}
              data-testid="btn-transfer-master"
              className="border border-rose-500/40 hover:bg-rose-500/10 text-rose-300 font-bold px-4 py-2 rounded-lg text-xs uppercase tracking-wider disabled:opacity-40"
            >
              {transferring ? "Transferindo..." : "Transferir Master"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
